import React from "react";
import styled from "@emotion/styled";

// Button Props Interface
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | 'destructive';
  size?: "sm" | "md" | "lg";
  selected?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

export const Button = styled.button<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  
  /* Size variants */
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: 8px 16px;
          font-size: 14px;
        `;
      case 'lg':
        return `
          padding: 16px 24px;
          font-size: 18px;
        `;
      default: // md
        return `
          padding: 14px 20px;
          font-size: 16px;
        `;
    }
  }}
  
  /* Full width */
  ${props => props.fullWidth && `
    width: 100%;
    flex: 1;
  `}
  
  /* Variant styles */
  ${props => {
    switch (props.variant) {
      case 'secondary':
        return `
          background-color: #f8f9fa;
          color: #212529;
          border: 1px solid #dee2e6;
          
          &:hover:not(:disabled) {
            background-color: #e9ecef;
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: #28a745;
          border: 1px solid #28a745;
          
          &:hover:not(:disabled) {
            background-color: #28a745;
            color: #fff;
          }
        `;
      case 'ghost':
        return `
          background-color: transparent;
          color: #28a745;
          border: none;
          
          &:hover:not(:disabled) {
            background-color: rgba(40, 167, 69, 0.1);
          }
        `;
      case 'destructive':
        return `
          background-color: #dc3545;
          color: #fff;
          
          &:hover:not(:disabled) {
            background-color: #882138;
          }
        `;
      default: // primary
        return `
          background-color: #28a745;
          color: #fff;
          
          &:hover:not(:disabled) {
            background-color: #218838;
          }
        `;
    }
  }}
  
  /* Disabled state */
  ${props => props.disabled && `
    opacity: 0.6;
  `}
    
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

